<template>
    <div
        ref="mapContainer"
        class="w-full h-full rounded overflow-hidden leaflet-route-map"
        :class="`max-h-[${maxHeight}px] min-h-[${minHeight}px]`"
        role="img"
        aria-labelledby="Route map from A to B"
    />
</template>

<script setup lang="ts">
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-routing-machine';
import 'leaflet-routing-machine/dist/leaflet-routing-machine.css';
import type { LatLng } from '@nuxt-monorepo/ui/types/components/Map/ApiProductMap';

const props = withDefaults(
    defineProps<{
        pointA: LatLng;
        pointB: LatLng;
        maxHeight?: number | string;
        minHeight?: number | string;
        /** Unique identifier for the route (e.g. airport code) */
        routeKey: string;
        /** S3 path where cached route data is stored */
        s3CachePath: string;
    }>(),
    {
        maxHeight: 700,
        minHeight: 500,
    }
);

const mapContainer = ref<HTMLDivElement | null>(null);

// Keep map/control refs for proper cleanup
let map: L.Map | null = null;
let routingControl: L.Control | null = null;

// Creates a Leaflet SVG marker icon with label
const createMarkerIcon = (label: string): L.Icon => {
    const svg = encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="42" viewBox="0 0 40 56">
      <path d="M20 0C9 0 0 9.6 0 21.4c0 11.8 20 34.6 20 34.6s20-22.8 20-34.6C40 9.6 31 0 20 0z" fill="#d93025"/>
      <text x="50%" y="44%" text-anchor="middle" fill="#fff" font-size="16" font-family="Arial" font-weight="bold" dominant-baseline="middle">${label}</text>
    </svg>
  `);
    return L.icon({
        iconUrl: `data:image/svg+xml,${svg}`,
        iconSize: [30, 42],
        iconAnchor: [15, 42],
    });
};

onMounted(() => {
    if (!process.client || !mapContainer.value || !('Routing' in L)) return;

    if (!(L.Routing as any).MapboxRouting) {
        console.error(
            '[LeafletRouteMap] MapboxRouting not available. Ensure leafletMapbox.client.ts plugin is loaded.'
        );
        return;
    }

    const { pointA, pointB, routeKey, s3CachePath } = props;

    // Validate coordinates
    const isFiniteLatLng = (p: LatLng) => Number.isFinite(p.lat) && Number.isFinite(p.lng);
    if (!isFiniteLatLng(pointA) || !isFiniteLatLng(pointB)) return;

    const center: [number, number] = [(pointA.lat + pointB.lat) / 2, (pointA.lng + pointB.lng) / 2];

    map = L.map(mapContainer.value, {
        attributionControl: false,
        zoomControl: false,
        scrollWheelZoom: true,
        dragging: true,
        doubleClickZoom: false,
        touchZoom: false,
    }).setView(center, 10);

    // Re-add attribution without the Leaflet prefix
    L.control.attribution({ prefix: '' }).addTo(map);

    // Tile layer must still provide attribution text
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors | ' +
            '<a href="https://www.mapbox.com/about/maps/" target="_blank" rel="noopener">© Mapbox</a>',
    }).addTo(map);

    if (!routeKey || !s3CachePath) {
        console.warn('[LeafletRouteMap] Missing routeKey or s3CachePath; routing is disabled.');
        return;
    }

    routingControl = L.Routing.control({
        waypoints: [L.latLng(pointA.lat, pointA.lng), L.latLng(pointB.lat, pointB.lng)],
        lineOptions: { styles: [{ color: '#2563eb', weight: 5 }] },
        createMarker: (i, wp) =>
            L.marker(wp.latLng, {
                icon: createMarkerIcon(i === 0 ? 'A' : 'B'),
            }),
        addWaypoints: false,
        draggableWaypoints: false,
        fitSelectedRoutes: true,
        showAlternatives: false,
        routeWhileDragging: false,
        router: new (L.Routing as any).MapboxRouting({
            key: routeKey,
            folder: s3CachePath,
        }),
        show: false,
        showRoute: true,
        summaryTemplate: '',
    }).addTo(map);
});

onBeforeUnmount(() => {
    // Remove routing control first
    try {
        routingControl?.remove?.();
    } catch {}
    routingControl = null;

    // Detach listeners and destroy map
    try {
        map?.off?.();
    } catch {}
    try {
        map?.remove?.();
    } catch {}
    map = null;
});
</script>

<style scoped>
.leaflet-container {
    z-index: 0;
    border-radius: 0;
}

.leaflet-route-map :deep(.leaflet-routing-container) {
    display: none !important;
}
</style>
